import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import useSWR from "swr";
import { useHistory, useLocation } from "react-router";
import { loadApi } from "../components/NotePlayer/VideoSync";
import { track } from "../utils/track";
import { feedOrder, gridOrder, learn } from "../utils/hotTaste";

/**
 * 핫클립 (2026-10, 새 디자인): 음악 쇼츠(유튜브 기타·우쿨렐레 연주)
 * - /hotclip : 인스타 돋보기처럼 3칸 격자 (서버 순위 + 내 취향 순, 2026-10-05 사장님)
 * - /hotclip?v=영상 : 고른 영상부터 위아래로 넘겨 보기. 다음 영상은 고른 영상과 비슷한 것 + 내 취향 (utils/hotTaste)
 * 영상은 유튜브 공식 플레이어로만 재생(하나를 두고 넘길 때마다 영상만 바꿈 — 소리가 계속 나오게).
 * 우리 악보와 연결된 영상은 '악보 보기' 버튼.
 */
export interface IHotClip {
  id: string;
  title: string;
  channel: string;
  views: number;
  tags?: string[]; // AI 가 붙인 연주 스타일·장르
  music: { id: number; title: string; singer: string } | null;
}

export const fmtViews = (n: number) => {
  if (n >= 100000000) return `${(n / 100000000).toFixed(1).replace(/\.0$/, "")}억`;
  if (n >= 10000) return `${(n / 10000).toFixed(n >= 100000 ? 0 : 1).replace(/\.0$/, "")}만`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}천`;
  return String(n);
};

// 쇼츠 세로 썸네일: oardefault 는 없는 영상이 많아(36개 중 10개) 회색 '…' 그림이 뜸 → oar2 (36개 모두 있음, 2026-10-05)
// 핫클립 선호 학습용 기록 (2026-10-05 사장님: 많이 누르고 오래 본 영상 스타일 위주로)
// hc:imp(첫 화면에 보임) · hc:clk(눌러서 엶) · hc:v(크게 봄) · hc:w:초(본 시간). 기록 규칙상 소문자만 → 영상 번호를 소문자로
const seenImp = new Set<string>();
export const hcEv = (kind: "imp" | "clk" | "v" | "w", id: string, extra?: number) => {
  if (kind === "imp") {
    if (seenImp.has(id)) return;
    seenImp.add(id);
  }
  track(`hc:${kind}:${id.toLowerCase()}${extra !== undefined ? ":" + extra : ""}`);
};

export const thumb = (id: string) => `https://i.ytimg.com/vi/${id}/oar2.jpg`;
// 유튜브는 없는 썸네일에도 작은 회색 그림(120x90)을 보내 onError 가 안 생김 → 크기로 알아보고 가로 썸네일로 바꿈
export const thumbFix = (id: string) => ({
  onError: (e: any) => ((e.target as HTMLImageElement).src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`),
  onLoad: (e: any) => {
    const im = e.target as HTMLImageElement;
    if (im.naturalWidth <= 120 && !im.src.endsWith("hqdefault.jpg")) im.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  },
});

const HotClips: React.FC = () => {
  const location = useLocation();
  const startId = new URLSearchParams(location.search).get("v");
  return startId ? <HotFeed startId={startId} /> : <HotGrid />;
};

// 격자: 썸네일 3칸, 누르면 그 영상부터 보기
const HotGrid: React.FC = () => {
  const history = useHistory();
  const { data } = useSWR<IHotClip[]>("/hotclips");
  const clips = useMemo(() => (Array.isArray(data) ? gridOrder(data) : []), [data]);
  useEffect(() => {
    track("hotclip:grid");
  }, []);
  return (
    <GridPage>
      <GridTop>
        <IconBtn type="button" aria-label="뒤로" onClick={() => (history.length > 1 ? history.goBack() : history.push("/"))}>
          <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" /></svg>
        </IconBtn>
        <h1>핫클립</h1>
        <small>요즘 인기 있는 기타 연주</small>
      </GridTop>
      {!clips.length && <Empty>{data ? "아직 핫클립이 없어요" : "불러오는 중…"}</Empty>}
      <Grid>
        {clips.map((c) => (
          <Tile
            key={c.id}
            type="button"
            onClick={() => {
              hcEv("clk", c.id);
              history.push(`/hotclip?v=${c.id}`);
            }}
          >
            <img
              src={thumb(c.id)}
              alt=""
              loading="lazy"
              onError={thumbFix(c.id).onError}
              onLoad={(e) => {
                thumbFix(c.id).onLoad(e);
                hcEv("imp", c.id);
              }}
            />
            <span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
              {fmtViews(c.views)}
            </span>
          </Tile>
        ))}
      </Grid>
    </GridPage>
  );
};

const HotFeed: React.FC<{ startId: string }> = ({ startId }) => {
  const history = useHistory();
  const { data } = useSWR<IHotClip[]>("/hotclips");
  // 처음 받은 목록으로 순서를 한 번만 정한다 (보는 도중 순서가 바뀌지 않게)
  const order = useRef<IHotClip[] | null>(null);
  if (!order.current && Array.isArray(data)) order.current = feedOrder(data, startId);
  const clips: IHotClip[] = order.current || [];
  const listRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [index, setIndex] = useState(0);
  const [scrolling, setScrolling] = useState(false);
  const started = useRef(false);

  // 고른 영상이 맨 앞(0번)이라 처음 위치는 맨 위
  useEffect(() => {
    if (!clips.length || started.current) return;
    started.current = true;
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [clips]);

  // 플레이어는 하나만 만들고 넘길 때마다 영상만 바꾼다
  useEffect(() => {
    const clip = clips[index];
    if (!clip) return;
    track("hotclip:view");
    hcEv("v", clip.id);
    const openedAt = Date.now();
    let sent = false;
    const sendWatch = () => {
      if (sent) return;
      sent = true;
      const sec = Math.min(300, Math.round((Date.now() - openedAt) / 1000));
      hcEv("w", clip.id, sec);
      learn(clip, sec); // 이 기기의 취향 배우기
    };
    window.addEventListener("pagehide", sendWatch);
    const stopWatch = () => {
      window.removeEventListener("pagehide", sendWatch);
      sendWatch();
    };
    if (playerRef.current && playerRef.current.loadVideoById) {
      playerRef.current.loadVideoById(clip.id);
      return stopWatch;
    }
    let alive = true;
    loadApi().then((YT) => {
      if (!alive || !boxRef.current || playerRef.current) return;
      playerRef.current = new YT.Player(boxRef.current, {
        videoId: clip.id,
        playerVars: { autoplay: 1, playsinline: 1, rel: 0, modestbranding: 1, loop: 1, playlist: clip.id },
        events: {
          onReady: (e: any) => e.target.playVideo(),
          // 끝나면 같은 영상을 다시 (쇼츠처럼)
          onStateChange: (e: any) => e.data === YT.PlayerState.ENDED && e.target.playVideo(),
        },
      });
    });
    return () => {
      alive = false;
      stopWatch();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, clips.length]);

  useEffect(
    () => () => {
      try {
        playerRef.current && playerRef.current.destroy();
      } catch (e) {}
    },
    []
  );

  // 손을 떼고 멈춘 칸이 지금 영상
  const endTimer = useRef<any>(null);
  const onScroll = () => {
    setScrolling(true);
    clearTimeout(endTimer.current);
    endTimer.current = setTimeout(() => {
      const el = listRef.current;
      if (!el) return;
      const i = Math.round(el.scrollTop / el.clientHeight);
      setScrolling(false);
      if (i !== index && clips[i]) setIndex(i);
    }, 140);
  };

  // 영상 위에서 위아래로 밀면 다음/이전, 톡 치면 멈춤/재생 (유튜브 화면이 터치를 가져가지 않게 투명 판을 덮음)
  const go = (d: number) => {
    const el = listRef.current;
    const i = Math.min(clips.length - 1, Math.max(0, index + d));
    if (!el || i === index) return;
    el.scrollTo({ top: i * el.clientHeight, behavior: "smooth" });
  };
  const pad = useRef<{ y: number; t: number } | null>(null);
  const onPadStart = (e: React.TouchEvent) => {
    pad.current = { y: e.touches[0].clientY, t: Date.now() };
  };
  const onPadEnd = (e: React.TouchEvent) => {
    const p = pad.current;
    pad.current = null;
    if (!p) return;
    const dy = e.changedTouches[0].clientY - p.y;
    if (Math.abs(dy) > 50) go(dy < 0 ? 1 : -1);
    else if (Date.now() - p.t < 400) togglePlay();
  };
  const togglePlay = () => {
    const pl = playerRef.current;
    if (!pl || !pl.getPlayerState) return;
    pl.getPlayerState() === 1 ? pl.pauseVideo() : pl.playVideo();
  };
  const wheelAt = useRef(0);
  const onWheel = (e: React.WheelEvent) => {
    if (Math.abs(e.deltaY) < 20 || Date.now() - wheelAt.current < 600) return;
    wheelAt.current = Date.now();
    go(e.deltaY > 0 ? 1 : -1);
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") go(1);
      if (e.key === "ArrowUp") go(-1);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });

  const cur = clips[index];

  return (
    <Page>
      <List ref={listRef} onScroll={onScroll}>
        {clips.map((c, i) => (
          <Slide key={c.id}>
            {Math.abs(i - index) <= 2 && <Thumb src={thumb(c.id)} alt="" {...thumbFix(c.id)} />}
          </Slide>
        ))}
        {!clips.length && (
          <Slide>
            <Empty>{data ? "아직 핫클립이 없어요" : "불러오는 중…"}</Empty>
          </Slide>
        )}
      </List>

      <PlayerLayer hidden={scrolling || !cur}>
        <div ref={boxRef} />
      </PlayerLayer>
      {cur && <TouchPad onTouchStart={onPadStart} onTouchEnd={onPadEnd} onWheel={onWheel} onClick={(e) => e.detail && !("ontouchstart" in window) && togglePlay()} />}

      <Top>
        <IconBtn type="button" aria-label="뒤로" onClick={() => (history.length > 1 ? history.goBack() : history.push("/hotclip"))}>
          <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" /></svg>
        </IconBtn>
        <h1>핫클립</h1>
        <span>{clips.length ? `${index + 1} / ${clips.length}` : ""}</span>
      </Top>

      {cur && (
        <Bottom>
          <Info>
            <strong>{cur.title}</strong>
            <span>
              {cur.channel} · 조회수 {fmtViews(cur.views)}
            </span>
          </Info>
          {cur.music && (
            <ScoreBtn
              type="button"
              onClick={() => {
                track("hotclip:score");
                history.push(`/notes/${cur.music!.id}`);
              }}
            >
              <svg viewBox="0 0 24 24"><path d="M9 18V5l11-2v13M9 18a3 3 0 1 1-3-3 3 3 0 0 1 3 3zM20 16a3 3 0 1 1-3-3 3 3 0 0 1 3 3z" /></svg>
              <span>
                <b>악보 보기</b>
                <small>
                  {cur.music.title} · {cur.music.singer}
                </small>
              </span>
            </ScoreBtn>
          )}
          <Hint>위로 밀면 다음 영상</Hint>
        </Bottom>
      )}
    </Page>
  );
};

export default HotClips;

const Page = styled.div`
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1100;
  background: #000;
  color: #f3f4f6;
  /* 넓은 화면: 가운데 세로 화면(9:16 비율 폭), 양옆은 검게 */
  @media (min-width: 600px) {
    width: min(100vw, 56.25vh, 520px);
    margin: 0 auto;
    box-shadow: 0 0 0 100vmax #000;
  }
`;

const List = styled.div`
  position: absolute;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  overscroll-behavior: contain;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slide = styled.div`
  height: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.85;
`;

const Empty = styled.p`
  color: #a3a9b5;
  font-size: 15px;
`;

// 영상은 화면 가운데 고정. 넘기는 동안은 숨기고 썸네일이 움직이게
const PlayerLayer = styled.div<{ hidden: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 56px;
  bottom: 150px;
  pointer-events: ${({ hidden }) => (hidden ? "none" : "auto")};
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  transition: opacity 0.15s;
  & iframe,
  & > div {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const TouchPad = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 56px;
  bottom: 150px;
  touch-action: none;
`;

const Top = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  padding: env(safe-area-inset-top) 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(#000c, #0000);
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
  & > h1 {
    flex: 1;
    margin: 0;
    font-size: 17px;
    font-weight: 700;
  }
  & > span {
    font-size: 13px;
    color: #a3a9b5;
    font-variant-numeric: tabular-nums;
  }
`;

const IconBtn = styled.button`
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 20px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  & > svg {
    width: 24px;
    height: 24px;
    fill: none;
    stroke: #f3f4f6;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const Bottom = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 150px;
  box-sizing: border-box;
  padding: 14px 16px calc(14px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: linear-gradient(#0000, #000e 30%);
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  & > strong {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }
  & > span {
    font-size: 12px;
    color: #a3a9b5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ScoreBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 14px;
  border-radius: 14px;
  border: 0;
  background: #ffb547;
  color: #0d0e12;
  text-align: left;
  cursor: pointer;
  & > svg {
    flex: none;
    width: 22px;
    height: 22px;
    fill: none;
    stroke: #0d0e12;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  & > span {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  & b {
    font-size: 15px;
  }
  & small {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Hint = styled.span`
  align-self: center;
  font-size: 11px;
  color: #7c8392;
`;

// ---------- 격자 화면 ----------
const GridPage = styled.div`
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1100;
  background: #0d0e12;
  color: #f3f4f6;
  overflow-y: auto;
  overscroll-behavior: contain;
  @media (min-width: 700px) {
    & > div {
      max-width: 760px;
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const GridTop = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 56px;
  padding: env(safe-area-inset-top) 8px 0;
  background: #0d0e12;
  & > h1 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
  }
  & > small {
    margin-left: 4px;
    font-size: 12px;
    color: #7c8392;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
  @media (min-width: 700px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const Tile = styled.button`
  position: relative;
  aspect-ratio: 9 / 16;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: #16181e;
  overflow: hidden;
  cursor: pointer;
  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  & > span {
    position: absolute;
    left: 6px;
    bottom: 6px;
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    text-shadow: 0 1px 3px #000c;
  }
  & > span > svg {
    width: 13px;
    height: 13px;
    fill: #fff;
  }
`;
